// "Add digital employee" from a role (TEMPLATES-T2, design §3 steps 3–4): the role's access step,
// shown under Channels & access. Required connections (each connected, or "connect later"; a
// family with no connector yet must be "connect later"), the applied vertical pack to use, the
// access per family (a level at most the overlay's cap; above the role's default it needs
// "Confirm wider access", which the API takes only from an owner or the DE's supervisor), and the
// board the role's routines go on. It edits the form and makes no call.
import {
  Checkbox,
  Field,
  FieldLabel,
  InfoTip,
  Label,
  NativeSelect,
  NativeSelectOption,
  Notice,
  SettingsSection,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { ShieldCheckIcon } from "lucide-react";
import { LoadError, Loading } from "../../screen-parts";
import { problemText } from "../../settings/problem-text";
import { DENSE_TABLE } from "../../settings/row-actions";
import { describeSchedule } from "../../structure/rrule";
import {
  type AccessFamily,
  type AccessLevel,
  FAMILY_LABELS,
  LEVEL_LABELS,
  levelsUpTo,
  levelText,
  type RolePlan,
  type TemplateOverlay,
  widens,
} from "../../templates/role-model";
import { ConnectionBadge } from "../../templates/templates-screen";
import type { Form } from "./model";

export function RoleAccessStep({
  plan,
  pending,
  error,
  form,
  set,
  packs,
  boards,
  applyError,
}: {
  plan: RolePlan | undefined;
  pending: boolean;
  error: unknown;
  form: Form;
  set: (patch: Partial<Form>) => void;
  /** The company's applied vertical pack overlays of this role. */
  packs: TemplateOverlay[];
  /** The boards routines may go on (null: none to offer). */
  boards: { id: string; name: string }[] | null;
  /** The last refusal of `applyRole` (403 widen, 422 connections), shown here. */
  applyError: unknown;
}) {
  const toggle = (list: AccessFamily[], f: AccessFamily, on: boolean) =>
    on ? [...new Set([...list, f])] : list.filter((x) => x !== f);
  return (
    <SettingsSection
      icon={ShieldCheckIcon}
      title="Role access"
      actions={
        <InfoTip label="About role access">
          It starts on probation at Draft level (at most With approval). Above the role's level
          needs an owner or its supervisor.
        </InfoTip>
      }
    >
      {pending ? (
        <Loading label="Loading the role" rows={3} />
      ) : error ? (
        <LoadError what="The role" error={error} />
      ) : plan ? (
        <div className="flex flex-col gap-5" data-slot="role-access-step">
          {packs.length ? (
            <Field>
              <FieldLabel htmlFor="add-de-pack">Vertical pack</FieldLabel>
              <NativeSelect
                id="add-de-pack"
                className="max-w-md"
                value={form.verticalOverlayId}
                onChange={(e) => set({ verticalOverlayId: e.target.value })}
              >
                <NativeSelectOption value="">None</NativeSelectOption>
                {packs.map((p) => (
                  <NativeSelectOption key={p.id} value={p.id}>
                    {p.name}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
          ) : null}

          {plan.required_connections.length ? (
            <fieldset className="flex flex-col gap-2" data-slot="role-connections">
              <legend className="mb-1 text-sm font-medium">Connections</legend>
              <ul className="flex flex-col divide-y divide-border rounded-md border border-border">
                {plan.required_connections.map((c) => {
                  const forced = c.status === "not_available";
                  const later = forced || form.connectLater.includes(c.family);
                  return (
                    <li
                      key={c.family}
                      data-family={c.family}
                      className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm"
                    >
                      <span className="min-w-24 font-medium">{FAMILY_LABELS[c.family]}</span>
                      <ConnectionBadge status={c.status} />
                      {c.why ? (
                        <InfoTip label={`Why ${FAMILY_LABELS[c.family]}`}>{c.why}</InfoTip>
                      ) : null}
                      {c.status !== "connected" ? (
                        <span className="ml-auto flex items-center gap-1.5">
                          <Checkbox
                            id={`later-${c.family}`}
                            checked={later}
                            disabled={forced}
                            onCheckedChange={(v) =>
                              set({
                                connectLater: toggle(form.connectLater, c.family, v === true),
                              })
                            }
                          />
                          <Label htmlFor={`later-${c.family}`} className="text-xs">
                            Connect later
                          </Label>
                        </span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </fieldset>
          ) : null}

          <fieldset className="flex flex-col gap-2" data-slot="role-access">
            <legend className="mb-1 flex items-center gap-1 text-sm font-medium">
              Access
              <InfoTip label="About access levels">
                Read · With approval · Direct · Manage. Capped by your overlay.
              </InfoTip>
            </legend>
            <Table className={DENSE_TABLE}>
              <TableHeader>
                <TableRow>
                  <TableHead>Family</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Cap</TableHead>
                  <TableHead>Level</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plan.access.map((a) => {
                  const value: AccessLevel = form.access[a.family] ?? a.level ?? "none";
                  const wider = widens(value, a.role);
                  return (
                    <TableRow key={a.family} data-family={a.family}>
                      <TableCell>{FAMILY_LABELS[a.family]}</TableCell>
                      <TableCell>{levelText(a.role)}</TableCell>
                      <TableCell>{a.cap ? levelText(a.cap) : "—"}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-2">
                          <NativeSelect
                            aria-label={`${FAMILY_LABELS[a.family]} level`}
                            className="h-8 w-36"
                            value={value}
                            onChange={(e) =>
                              set({
                                access: {
                                  ...form.access,
                                  [a.family]: e.target.value as AccessLevel,
                                },
                              })
                            }
                          >
                            {levelsUpTo(a.cap).map((l) => (
                              <NativeSelectOption key={l} value={l}>
                                {LEVEL_LABELS[l]}
                              </NativeSelectOption>
                            ))}
                          </NativeSelect>
                          {wider ? (
                            <span className="flex items-center gap-1.5" data-slot="confirm-widen">
                              <Checkbox
                                id={`widen-${a.family}`}
                                checked={form.confirmWiden.includes(a.family)}
                                onCheckedChange={(v) =>
                                  set({
                                    confirmWiden: toggle(form.confirmWiden, a.family, v === true),
                                  })
                                }
                              />
                              <Label htmlFor={`widen-${a.family}`} className="text-xs">
                                Confirm wider access
                              </Label>
                            </span>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </fieldset>

          {plan.cadence.length ? (
            <div className="flex flex-col gap-2" data-slot="role-routines">
              <Field>
                <FieldLabel htmlFor="add-de-routines">Put routines on</FieldLabel>
                <NativeSelect
                  id="add-de-routines"
                  className="max-w-md"
                  value={form.routineBoardId}
                  onChange={(e) => set({ routineBoardId: e.target.value })}
                >
                  <NativeSelectOption value="">No routines</NativeSelectOption>
                  {(boards ?? []).map((b) => (
                    <NativeSelectOption key={b.id} value={b.id}>
                      {b.name}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              </Field>
              <ul className="flex flex-col gap-0.5 text-sm">
                {plan.cadence.map((c) => (
                  <li key={c.key}>
                    {c.name}{" "}
                    <span className="text-muted-foreground">· {describeSchedule(c.rrule)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {applyError ? (
            <Notice tone="danger" role="alert" data-slot="role-apply-error">
              {problemText(applyError, "The role was not applied.")}
            </Notice>
          ) : null}
        </div>
      ) : null}
    </SettingsSection>
  );
}
